import React, { useContext, useEffect, useState } from "react";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import Switch from "@mui/material/Switch";
import * as FP from "utils/fp-js";

import {
  Box,
  Button,
  IconButton,
  InputLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { required } from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { FormSelectField } from "UI/FormSelectField";
import PlusIcon from "@mui/icons-material/Add";
import MinusIcon from "@mui/icons-material/Remove";
import { useNavigate } from "react-router-dom";
import { usePracticeOptions } from "hooks/usePracticeOptions";

const invoiceItemDescriptions = [
  {
    item_type: "Notification",
    item_description: "Notifications",
  },
  {
    item_type: "Question",
    item_description: "Questions",
  },
  {
    item_type: "Referral",
    item_description: "Referrals",
  },
  {
    item_type: "Button Device",
    item_description: "Physical button device",
  },
  {
    item_type: "Service",
    item_description: "Additional service",
  },
];

const getPriceByType = (type, recipient) => {
  switch (type) {
    case "Notification":
      return recipient?.notification_price;
    case "Question":
      return recipient?.question_price;
    case "Referral":
      return recipient?.referral_price;
    case "Button Device":
      return recipient?.button_device_price;
    default:
      return 0;
  }
};

const getQuantityWithoutFreeClicks = (quantity, freeClicks) => {
  return quantity > freeClicks ? quantity - freeClicks : 0;
};

const defaultFormData = {
  recipient_id: "",
  total_amount: 0,
};

export const AddNewInvoiceDialog = ({ open, handleClose, refreshList }) => {
  const { createApiCall } = useCreateApiCall();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showWarning, userData } = useContext(GlobalContext);
  const { practicesOptions, practices } = usePracticeOptions();
  /** @type {{ item_description: string; item_type; quantity: number; unit_price: number, }[]} */
  const defaultItems = [];
  const [items, setItems] = useState(defaultItems);
  const [superAdminStatistics, setSuperAdminStatistics] = useState(null);
  const [statistics, setStatistics] = useState(null);

  const { control, handleSubmit, getValues, reset, watch } = useForm({
    defaultValues: defaultFormData,
  });

  const closeAndReset = () => {
    handleClose();
    setTimeout(() => {
      reset(defaultFormData);
      setItems([]);
    }, 300);
  };

  const recipientId = watch("recipient_id");

  const loadLastInvoiceDateByRecipientId = (recipientId) =>
    createApiCall(async () => {
      const result = await API.get(
        "restApi",
        `/last_invoice_info/${recipientId}`,
        {}
      );

      if (result?.error) {
        console.error(result.error);
        dispatch(showError(result.error));
        return;
      }

      setStatistics(result);
    });

  const onSubmit = createApiCall(async () => {
    const invoice = getValues();
    const positions = items?.reduce((acc, { quantity }) => acc + quantity, 0);

    if (!positions) {
      showWarning({
        title: "No items",
        content:
          "Please ensure that you add at least one item to your invoice to proceed.",
      });
      return;
    }

    invoice.total_amount = items.reduce(
      (acc, { quantity, unit_price }) => acc + quantity * Number(unit_price),
      0
    );

    const result = await API.put("restApi", "/invoices", {
      body: {
        invoice,
        invoice_items: items.filter(({ quantity }) => quantity > 0),
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    // showWarning({
    //   title: (
    //     <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
    //       <Typography variant="h6" sx={{ fontWeight: "bold" }}>
    //         {`Invoice ${
    //           result?.lastInsertInvoiceId
    //             ? `#${result?.lastInsertInvoiceId}`
    //             : ""
    //         } added successfully`}
    //       </Typography>
    //     </Stack>
    //   ),
    //   content: <></>,
    //   asNotification: true,
    // });

    if (result?.lastInsertInvoiceId) {
      navigate(`/invoice/${result?.lastInsertInvoiceId}`);
      return;
    }
    closeAndReset();
    refreshList();
  });

  const loadRecipientsStatistics = createApiCall(async () => {
    const result = await API.get("restApi", `/last_invoice_info`, {});

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    setSuperAdminStatistics(result);
  });

  useEffect(() => {
    if (!open && userData?.role === "Super Admin") {
      loadRecipientsStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userData]);

  useEffect(() => {
    setStatistics(null);

    if (open && recipientId && practices?.length) {
      const recipient = practices.find(({ id }) => id === recipientId);
      const initialItems = invoiceItemDescriptions.reduce(
        (acc, { item_type, item_description }) => {
          return [
            ...acc,
            {
              item_description,
              item_type,
              quantity: 0,
              unit_price: Number(getPriceByType(item_type, recipient)),
            },
          ];
        },
        /** @type {{ item_description: string; item_type; quantity: number; unit_price: number, }} */
        []
      );
      setItems(initialItems);

      loadLastInvoiceDateByRecipientId(recipientId)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipientId, practices]);

  const increaseQuantity = (index) => {
    const newItems = [...items];
    newItems[index].quantity = items[index].quantity + 1;
    setItems(newItems);
  };

  const reduceQuantity = (index) => {
    const newItems = [...items];
    const newValue = items[index].quantity - 1;
    newItems[index].quantity = newValue >= 0 ? newValue : 0;
    setItems(newItems);
  };

  const handleClickSwitch = (e, type, index) => {
    const value = e?.target?.checked;
    const newItems = [...items];

    if (value) {
      newItems[index].quantity = FP.match(type)
        .on(
          "Notification",
          getQuantityWithoutFreeClicks(
            statistics?.uninvoicedNotifications,
            statistics?.freeClicks?.freeNotifications
          )
        )
        .on("Question",
          getQuantityWithoutFreeClicks(
            statistics?.uninvoicedQuestions,
            statistics?.freeClicks?.freeQuestions
          )
        )
        .on("Referral", 
          getQuantityWithoutFreeClicks(
            statistics?.uninvoicedReferrals,
            statistics?.freeClicks?.freeReferrals
          )
        )
        .otherwise(0);
    } else {
      newItems[index].quantity = 0;
    }
    setItems(newItems);
  };

  const practicesWithUninvoicedReferrals = [];
  const practicesWithoutUninvoicedReferrals = [];

  Object.entries(practicesOptions).forEach(([key, value]) => {
    const uninvoicedNotificationsCount =
      superAdminStatistics?.uninvoicedNotifications?.find(
        ({ recipientId }) => key === recipientId
      )?.quantity ?? 0;
    const uninvoicedQuestionsCount =
      superAdminStatistics?.uninvoicedQuestions?.find(
        ({ recipientId }) => key === recipientId
      )?.quantity ?? 0;
    const uninvoicedReferralsCount =
      superAdminStatistics?.uninvoicedReferrals?.find(
        ({ recipientId }) => key === recipientId
      )?.quantity ?? 0;

    const count =
      uninvoicedNotificationsCount +
      uninvoicedQuestionsCount +
      uninvoicedReferralsCount;
    if (count > 0) {
      practicesWithUninvoicedReferrals.push([key, `(${count}) ${value}`]);
    } else {
      practicesWithoutUninvoicedReferrals.push([key, value]);
    }
  });

  const recipientOptions = [
    ...practicesWithUninvoicedReferrals,
    ...practicesWithoutUninvoicedReferrals,
  ].reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={closeAndReset}
      title={"Create new invoice"}
      content={
        <Box sx={{ maxWidth: "640px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack alignItems={"flex-start"} flex={1}>
              <InputLabel htmlFor="recipient_id">Recipient</InputLabel>
              <FormSelectField
                id="recipient_id"
                name="recipient_id"
                control={control}
                options={recipientOptions}
                withNone={false}
                rules={{ required }}
                doNotSortOptions
              />
            </Stack>
            {!!items?.length && (
              <>
                <Typography
                  variant="h6"
                  sx={{ alignSelf: "flex-start", marginTop: "20px" }}
                >
                  Select Invoice Items
                </Typography>
                <div className="text-sm text-gray-500 text-left mb-4">
                  Use the form below to include or exclude items from your new
                  invoice. For questions and referrals, simply switch the slider
                  to the right to add the item or to the left to remove it. For
                  Button Device and Additional Service, use the plus (+) and
                  minus (–) buttons to adjust the quantity of each item. Each
                  control corresponds to an invoice item:
                </div>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="none">Description</TableCell>
                      <TableCell padding="none" align="center" width={"140px"}>
                        Quantity
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items?.map(
                      (
                        { item_type, item_description, quantity, unit_price },
                        index
                      ) => {
                        if (
                          ("Notification" === item_type &&
                            statistics?.uninvoicedNotifications > 0) ||
                          ("Question" === item_type &&
                            statistics?.uninvoicedQuestions > 0) ||
                          ("Referral" === item_type &&
                            statistics?.uninvoicedReferrals > 0)
                        ) {
                          return (
                            <TableRow key={item_type}>
                              <TableCell padding="none">
                                {item_description}
                              </TableCell>
                              <TableCell
                                padding="none"
                                align="center"
                                width={"100px"}
                              >
                                <Stack
                                  direction={"row"}
                                  gap={1}
                                  alignItems={"center"}
                                >
                                  Off
                                  <Switch
                                    onClick={(e) =>
                                      handleClickSwitch(e, item_type, index)
                                    }
                                  />
                                  {FP.match(item_type)
                                    .on(
                                      "Notification",
                                      getQuantityWithoutFreeClicks(
                                        statistics?.uninvoicedNotifications,
                                        statistics?.freeClicks
                                          ?.freeNotifications
                                      )
                                    )
                                    .on(
                                      "Question",
                                      getQuantityWithoutFreeClicks(
                                        statistics?.uninvoicedQuestions,
                                        statistics?.freeClicks?.freeQuestions
                                      )
                                    )
                                    .on(
                                      "Referral",
                                      getQuantityWithoutFreeClicks(
                                        statistics?.uninvoicedReferrals,
                                        statistics?.freeClicks?.freeReferrals
                                      )
                                    )
                                    .otherwise(0)}
                                </Stack>
                              </TableCell>
                              {/* <TableCell>{USDollar.format(unit_price)}</TableCell> */}
                            </TableRow>
                          );
                        } else if (
                          item_type === "Button Device" ||
                          item_type === "Service"
                        ) {
                          return (
                            <TableRow key={item_type}>
                              <TableCell padding="none">
                                {item_description}
                              </TableCell>
                              <TableCell
                                padding="none"
                                align="center"
                                width={"140px"}
                              >
                                <Stack
                                  direction={"row"}
                                  gap={1}
                                  alignItems={"center"}
                                >
                                  <IconButton
                                    onClick={() => reduceQuantity(index)}
                                  >
                                    <MinusIcon fontSize="small" />
                                  </IconButton>
                                  {quantity}
                                  <IconButton
                                    onClick={() => increaseQuantity(index)}
                                  >
                                    <PlusIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                              {/* <TableCell>{USDollar.format(unit_price)}</TableCell> */}
                            </TableRow>
                          );
                        }

                        return null;
                      }
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </form>
        </Box>
      }
      actions={
        <Stack
          mt={2}
          mb={2}
          width={"100%"}
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={1}
        >
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            Create new invoice
          </Button>
        </Stack>
      }
    />
  );
};
